import React, { useState, useEffect } from 'react';
import { createAgenturTeam, getTeamsByAgentur } from '../../api';

function MemberCardForm({ member, teams, onSave, onCancel, agenturId, onTeamCreated, user, passedAgenturTeams = [] }) {
  const [formData, setFormData] = useState({
    email: member?.email || '',
    fullName: member?.fullName || '',
    password: member?.password || '',
    confirmPassword: member?.confirmPassword || '',
    roleType: member?.roleType || '0',
    teamId: member?.teamId || '',
    a_team_id: member?.a_team_id || ''
  });

  const [errors, setErrors] = useState({});
  
  // Teams-Verwaltung
  const [agenturTeams, setAgenturTeams] = useState([]);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: '', beschreibung: '' });
  const [teamErrors, setTeamErrors] = useState({});
  const [teamLoading, setTeamLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Fehler zurücksetzen
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Team-Änderungen
  const handleTeamChange = (e) => {
    const { name, value } = e.target;
    setNewTeam(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Fehler zurücksetzen
    if (teamErrors[name]) {
      setTeamErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Teams für die Agentur laden - verwende übergebene passedAgenturTeams oder lade von API
  const loadAgenturTeams = async () => {
    if (passedAgenturTeams && passedAgenturTeams.length > 0) {
      // Verwende die übergebenen Teams
      console.log('Verwende übergebene Teams:', passedAgenturTeams);
      setAgenturTeams(passedAgenturTeams);
      return;
    }
    
    if (!agenturId) {
      console.log('Keine agenturId, setze leere Teams');
      setAgenturTeams([]);
      return;
    }
    
    try {
      const response = await getTeamsByAgentur(user.id, agenturId);
      if (response.success) {
        setAgenturTeams(response.teams || []);
      } else {
        console.error('Fehler beim Laden der Teams:', response.error);
        setAgenturTeams([]);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Teams:', err);
      setAgenturTeams([]);
    }
  };

  // Team erstellen
  const handleCreateTeam = async () => {
    const newTeamErrors = {};
    
    if (!newTeam.name.trim()) {
      newTeamErrors.name = 'Team-Name ist erforderlich';
    }
    
    if (!newTeam.beschreibung.trim()) {
      newTeamErrors.beschreibung = 'Beschreibung ist erforderlich';
    }
    
    setTeamErrors(newTeamErrors);
    
    if (Object.keys(newTeamErrors).length > 0) {
      return;
    }
    
    setTeamLoading(true);
    
    try {
      const response = await createAgenturTeam(user.id, agenturId, newTeam.name, newTeam.beschreibung);
      
      if (response.success) {
        // Team erfolgreich erstellt - Liste neu laden
        await loadAgenturTeams();
        setNewTeam({ name: '', beschreibung: '' });
        setShowTeamForm(false);
        
        // Callback aufrufen, falls vorhanden
        if (onTeamCreated) {
          onTeamCreated(response.team);
        }
        
        console.log('Team erstellt:', response.team);
      } else {
        setTeamErrors({ general: response.error || 'Fehler beim Erstellen des Teams' });
      }
    } catch (err) {
      console.error('Fehler beim Erstellen des Teams:', err);
      setTeamErrors({ general: 'Netzwerkfehler beim Erstellen des Teams' });
    } finally {
      setTeamLoading(false);
    }
  };

  // Teams beim Laden der Komponente laden
  useEffect(() => {
    loadAgenturTeams();
  }, [agenturId, passedAgenturTeams]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'E-Mail ist ungültig';
    }

    if (!formData.fullName) {
      newErrors.fullName = 'Vollständiger Name ist erforderlich';
    }

    if (!formData.password) {
      newErrors.password = 'Passwort ist erforderlich';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Passwort muss mindestens 6 Zeichen lang sein';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwörter stimmen nicht überein';
    }

    if (!formData.roleType) {
      newErrors.roleType = 'Rolle ist erforderlich';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Passwort-Bestätigung entfernen vor dem Speichern
      const { confirmPassword, ...memberData } = formData;
      onSave(memberData);
    }
  };

  const getRoleName = (roleType) => {
    switch (parseInt(roleType, 10)) {
      case 1: return 'Administrator';
      case 2: return 'Teamleiter';
      case 3: return 'Organisationsleiter';
      case 4: return 'Callcenter Leader';
      case 5: return 'Callcenter Mitarbeiter';
      case 6: return 'Fachberater';
      case 7: return 'Agentur Leiter';
      default: return 'Benutzer';
    }
  };

  return (
    <div className="modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1001
    }}>
      <div className="modal-dialog" style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        <div className="modal-header" style={{
          display: 'flex',
          alignItems: 'center',
          padding: '20px',
          borderBottom: '1px solid #e2e8f0',
          marginBottom: '20px',
          position: 'relative'
        }}>
          <h3 style={{ 
            margin: 0, 
            fontSize: '1.3rem', 
            fontWeight: '600',
            width: '80%',
            textAlign: 'left'
          }}>
            {member ? 'Mitglied bearbeiten' : 'Neues Mitglied hinzufügen'}
          </h3>
          <button 
            className="close-button" 
            onClick={onCancel}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              width: '40px',
              height: '40px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s',
              marginLeft: 'auto',
              flexShrink: 0
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f1f5f9'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        
        <div className="modal-content" style={{ padding: '0 20px 20px 20px' }}>
          <form onSubmit={handleSubmit}>
            {/* Benutzerdaten */}
            <div className="form-section" style={{ marginBottom: '25px' }}>
              <h4 style={{ margin: '0 0 15px 0', fontSize: '1rem', fontWeight: '600' }}>
                Benutzerdaten
              </h4>
              
              <div className="form-row" style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                <div className="form-field" style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    E-Mail*
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: errors.email ? '1px solid #dc2626' : '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  {errors.email && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{errors.email}</div>}
                </div>
                <div className="form-field" style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Vollständiger Name*
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: errors.fullName ? '1px solid #dc2626' : '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  {errors.fullName && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{errors.fullName}</div>}
                </div>
              </div>
              
              <div className="form-row" style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                <div className="form-field" style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Passwort*
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: errors.password ? '1px solid #dc2626' : '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  {errors.password && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{errors.password}</div>}
                </div>
                <div className="form-field" style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Passwort bestätigen*
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: errors.confirmPassword ? '1px solid #dc2626' : '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  {errors.confirmPassword && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{errors.confirmPassword}</div>}
                </div>
              </div>
            </div>

            {/* Teams für Agentur */}
            {agenturId && (
              <div className="form-section" style={{ marginBottom: '25px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>
                    Agentur-Teams
                  </h4>
                  <button
                    type="button"
                    onClick={() => setShowTeamForm(!showTeamForm)}
                    style={{
                      padding: '6px 12px',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    {showTeamForm ? 'Abbrechen' : '+ Neues Team'}
                  </button>
                </div>

                {/* Team-Erstellungsformular */}
                {showTeamForm && (
                  <div style={{ 
                    padding: '15px', 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '6px', 
                    backgroundColor: '#f9fafb',
                    marginBottom: '15px'
                  }}>
                    <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '14px' }}>
                          Team-Name*
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={newTeam.name}
                          onChange={handleTeamChange}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: teamErrors.name ? '1px solid #dc2626' : '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px'
                          }}
                          placeholder="Name des Teams"
                        />
                        {teamErrors.name && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{teamErrors.name}</div>}
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '14px' }}>
                          Beschreibung*
                        </label>
                        <input
                          type="text"
                          name="beschreibung"
                          value={newTeam.beschreibung}
                          onChange={handleTeamChange}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: teamErrors.beschreibung ? '1px solid #dc2626' : '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px'
                          }}
                          placeholder="Beschreibung des Teams"
                        />
                        {teamErrors.beschreibung && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{teamErrors.beschreibung}</div>}
                      </div>
                    </div>
                    
                    {/* Allgemeine Fehleranzeige */}
                    {teamErrors.general && (
                      <div style={{ 
                        color: '#dc2626', 
                        fontSize: '12px', 
                        marginBottom: '10px',
                        padding: '8px',
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '4px'
                      }}>
                        {teamErrors.general}
                      </div>
                    )}
                    
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                      <button
                        type="button"
                        onClick={() => {
                          setShowTeamForm(false);
                          setNewTeam({ name: '', beschreibung: '' });
                          setTeamErrors({});
                        }}
                        style={{
                          padding: '6px 12px',
                          border: '1px solid #d1d5db',
                          background: 'white',
                          color: '#374151',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Abbrechen
                      </button>
                      <button
                        type="button"
                        onClick={handleCreateTeam}
                        disabled={teamLoading}
                        style={{
                          padding: '6px 12px',
                          background: teamLoading ? '#9ca3af' : '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: teamLoading ? 'not-allowed' : 'pointer',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}
                      >
                        {teamLoading ? 'Erstelle...' : 'Team erstellen'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Bestehende Teams anzeigen */}
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {agenturTeams.length === 0 ? (
                    <div style={{ 
                      textAlign: 'center', 
                      color: '#6b7280', 
                      fontStyle: 'italic', 
                      padding: '20px',
                      border: '1px dashed #d1d5db',
                      borderRadius: '6px'
                    }}>
                      Keine Teams vorhanden
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {agenturTeams.map((team, index) => (
                        <div key={index} style={{
                          padding: '12px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          backgroundColor: 'white'
                        }}>
                          <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                            {team.name}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            {team.beschreibung || 'Keine Beschreibung'}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Rolle und Team */}
            <div className="form-section" style={{ marginBottom: '25px' }}>
              <h4 style={{ margin: '0 0 15px 0', fontSize: '1rem', fontWeight: '600' }}>
                Rolle und Team
              </h4>
              
              <div className="form-row" style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                <div className="form-field" style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Rolle*
                  </label>
                  <select
                    name="roleType"
                    value={formData.roleType}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: errors.roleType ? '1px solid #dc2626' : '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="0">Normaler Benutzer</option>
                    <option value="1">Administrator</option>
                    <option value="2">Teamleiter</option>
                    <option value="3">Organisationsleiter</option>
                    <option value="4">Callcenter Leader</option>
                    <option value="5">Callcenter Mitarbeiter</option>
                    <option value="6">Fachberater</option>
                    <option value="7">Agentur Leiter</option>
                  </select>
                  {errors.roleType && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{errors.roleType}</div>}
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    {getRoleName(formData.roleType)}
                  </div>
                </div>
                <div className="form-field" style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Projekt
                  </label>
                  <select
                    name="teamId"
                    value={formData.teamId}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">-- Kein Team --</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name} {team.isDefault ? '(Standard)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Agentur-Team Auswahl */}
              <div className="form-row" style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                <div className="form-field" style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Agentur-Team (optional)
                  </label>
                  <select
                    name="a_team_id"
                    value={formData.a_team_id}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">-- Kein Agentur-Team --</option>
                    {agenturTeams.map((team, index) => (
                      <option key={index} value={index}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                  {agenturTeams.length === 0 && (
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Noch keine Teams erstellt. Erstellen Sie zuerst Teams in der Agentur-Verwaltung.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Aktionen */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              marginTop: '20px',
              paddingTop: '15px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                type="button"
                onClick={onCancel}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  background: 'white',
                  color: '#374151',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Abbrechen
              </button>
              <button
                type="submit"
                style={{
                  padding: '8px 16px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {member ? 'Aktualisieren' : 'Hinzufügen'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default MemberCardForm;
